import { PenLine, RefreshCw, Save } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api';
import type { SignatureBatch, SignatureQueueItem, SignatureSettings } from '../../../api/areas/postOperations';
import { Badge, type Tone } from '../../../components/Badge';
import { DataTable, Field, Section, type Column } from '../../../components/blocks';
import { EntityRef } from '../../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { EmptyState, ErrorState, Loading } from '../../../components/States';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SigningServiceNotice } from '../../../components/SigningServiceNotice';
import { SigningError } from '../../../components/SigningError';
import { loadSigningService } from '../../../lib/signingService';
import { useApi } from '../../../lib/useApi';
import { FormActions, FormGrid, MfaHint, SelectField, SuccessNote, When } from '../ui';
import { useWrite, WriteError } from './shared';

const BATCH_TONE: Record<SignatureBatch['status'], Tone> = {
  EMPTY: 'neutral',
  QUEUED: 'pending',
  SENT: 'pending',
  WAITING_APPROVAL: 'pending',
  SIGNED: 'ok',
  PARTIALLY_FAILED: 'warn',
  EXPIRED: 'serious',
  REJECTED: 'serious',
  FAILED: 'crit',
};

const ITEM_TONE: Record<string, Tone> = {
  PENDING_CHECK: 'pending',
  REVIEW: 'warn',
  QUEUED: 'pending',
  IN_BATCH: 'info',
  SIGNED: 'ok',
  CANCELLED: 'neutral',
  PENDING: 'pending',
  MISMATCH: 'crit',
  FAILED: 'crit',
  EXPIRED: 'serious',
  REJECTED: 'serious',
};

export function SignatureSettingsForm({ initial, onSaved }: { initial: SignatureSettings; onSaved: () => void }) {
  const { t } = useTranslation('compliance');
  const [s, setS] = useState<SignatureSettings>(initial);
  const write = useWrite((body: SignatureSettings, opts) => api.updateSignatureSettings(body, opts));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await write.run(s)) onSaved();
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <Field label={t('signature.settings.dailyAt')} hint={t('signature.settings.dailyAtHint')}>
          <Input value={s.daily_at} onChange={(e) => setS({ ...s, daily_at: e.target.value })} pattern="^([01]\d|2[0-3]):[0-5]\d$" required />
        </Field>
        <Field label={t('signature.settings.chunk')} hint={t('signature.settings.chunkHint')}>
          <Input type="number" min={1} max={100} value={s.chunk_size} onChange={(e) => setS({ ...s, chunk_size: Number(e.target.value) })} required />
        </Field>
        <Field label={t('signature.settings.window')}>
          <Input
            type="number"
            min={60}
            max={3600}
            value={s.approval_window_seconds}
            onChange={(e) => setS({ ...s, approval_window_seconds: Number(e.target.value) })}
            required
          />
        </Field>
        <SelectField
          label={t('signature.settings.signer')}
          value={s.signer}
          options={(['SMARTCA', 'OFF'] as const).map((v) => ({ value: v, label: t(`signature.signer.${v}`) }))}
          onChange={(v) => v && setS({ ...s, signer: v })}
        />
      </FormGrid>
      <MfaHint permission="regulatory_report.manage" />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      {write.state.phase === 'done' ? <SuccessNote>{t('signature.settings.saved')}</SuccessNote> : null}
      <FormActions>
        <Button type="submit" size="sm" disabled={write.busy}>
          <Save /> {t('signature.settings.save')}
        </Button>
      </FormActions>
    </form>
  );
}

function BatchDetail({ batch }: { batch: SignatureBatch }) {
  const { t } = useTranslation('compliance');
  return (
    <div className="grid gap-2 text-xs">
      {batch.transactions.map((x) => (
        <div key={x.id} className="flex flex-wrap items-center gap-2">
          <span>{t('signature.transaction', { n: x.chunk_no, count: x.item_count })}</span>
          <Badge tone={ITEM_TONE[x.status] ?? 'neutral'}>{t(`signature.batchStatus.${x.status}`, { defaultValue: x.status })}</Badge>
          {x.expires_at ? (
            <span className="text-muted-foreground">
              {t('signature.expires')} <When at={x.expires_at} />
            </span>
          ) : null}
        </div>
      ))}
      {batch.items.map((i) => (
        <div key={i.id} className="flex flex-wrap items-center gap-2">
          <code>{i.doc_id}</code>
          <span>{i.ascii_name}</span>
          <Badge tone={ITEM_TONE[i.status] ?? 'neutral'}>{t(`signature.itemStatus.${i.status}`)}</Badge>
          {i.error ? <span className="text-muted-foreground">{i.error}</span> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * "Chờ NĐK ký" (ADR-024): dossiers waiting for the registrar's digital signature, the daily
 * batch sent to the signing service (SmartCA: up to 100 documents per confirmation) and its
 * results, "Gửi ký ngay", and the batch settings.
 */
export function RegistrarSignaturePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  useDocumentTitle(t('signature.title'));
  const [version, setVersion] = useState(0);
  const queue = useApi((signal) => api.listSignatureQueue({}, { signal }), [version]);
  const batches = useApi((signal) => api.listSignatureBatches({ signal }), [version]);
  const signNow = useWrite((_: null, opts) => api.signQueuedNow({ all: true }, opts));
  const service = useApi((signal) => loadSigningService(signal), [version]);
  const refresh = () => setVersion((v) => v + 1);

  const queueColumns: Column<SignatureQueueItem>[] = [
    { key: 'domain', header: t('signature.col.domain'), cell: (r) => <EntityRef label={r.ascii_name} to={`/domains/${encodeURIComponent(r.ascii_name)}`} id={r.domain_id} /> },
    { key: 'status', header: t('signature.col.status'), cell: (r) => <Badge tone={ITEM_TONE[r.status] ?? 'neutral'}>{t(`signature.queueStatus.${r.status}`)}</Badge> },
    { key: 'attempts', header: t('signature.col.attempts'), numeric: true, cell: (r) => r.attempts },
    {
      key: 'mismatch',
      header: t('signature.col.mismatches'),
      numeric: true,
      cell: (r) => (r.mismatches > 0 ? <Badge tone="crit">{r.mismatches}</Badge> : 0),
    },
    { key: 'queued', header: t('signature.col.queued'), cell: (r) => <When at={r.queued_at} /> },
    {
      key: 'precheck',
      header: t('signature.col.precheck'),
      cell: (r) => {
        const failed = (r.precheck ?? []).filter((c) => c.status !== 'PASS');
        if (!r.prechecked_at) return <span className="text-muted-foreground">—</span>;
        return failed.length ? (
          <span className="grid gap-0.5 text-xs">
            {failed.map((c) => (
              <span key={c.code}>{c.message ?? c.code}</span>
            ))}
            {r.precheck_work_item_id ? (
              <Link to={`/compliance/work/${encodeURIComponent(r.precheck_work_item_id)}`} className="text-primary hover:underline">
                {t('signature.openPrecheck')}
              </Link>
            ) : null}
          </span>
        ) : (
          <Badge tone="ok">{t('signature.precheckPassed')}</Badge>
        );
      },
    },
    { key: 'error', header: t('signature.col.lastError'), cell: (r) => r.last_error ?? <span className="text-muted-foreground">—</span> },
  ];

  const batchColumns: Column<SignatureBatch>[] = [
    { key: 'date', header: t('signature.col.date'), cell: (b) => `${b.batch_date} · ${t(`signature.trigger.${b.trigger}`)}` },
    { key: 'status', header: t('signature.col.status'), cell: (b) => <Badge tone={BATCH_TONE[b.status]}>{t(`signature.batchStatus.${b.status}`)}</Badge> },
    { key: 'counts', header: t('signature.col.counts'), cell: (b) => t('signature.counts', { signed: b.signed_count, failed: b.failed_count, total: b.item_count }) },
    { key: 'signer', header: t('signature.col.signer'), cell: (b) => <code className="text-xs">{b.signer}</code> },
    { key: 'detail', header: t('signature.col.detail'), cell: (b) => <BatchDetail batch={b} /> },
  ];

  const settings = batches.status === 'success' ? batches.data.settings : undefined;

  return (
    <>
      <PageHeader
        title={t('signature.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('signature.subtitle', { at: settings?.daily_at ?? '16:00' })}
        actions={
          <>
            <Button size="sm" variant="outline" onClick={refresh}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
            <Button
              size="sm"
              disabled={signNow.busy}
              onClick={async () => {
                if (await signNow.run(null)) refresh();
              }}
            >
              <PenLine /> {t('signature.signNow')}
            </Button>
          </>
        }
      />
      <div className="space-y-6">
        {signNow.state.phase === 'error' ? <SigningError error={signNow.state.error} /> : null}
        {signNow.state.phase === 'done' ? <SuccessNote>{t('signature.signNowDone', { count: signNow.state.result.batch.item_count })}</SuccessNote> : null}
        <SigningServiceNotice service={service.status === 'success' ? service.data : null} />
        <Section flush title={t('signature.queueTitle')}>
          {queue.status === 'loading' ? <Loading label={t('signature.loading')} rows={3} /> : null}
          {queue.status === 'error' ? <ErrorState error={queue.error} onRetry={queue.reload} title={t('signature.loadError')} /> : null}
          {queue.status === 'success' ? (
            <DataTable columns={queueColumns} rows={queue.data.items} rowKey={(r) => r.id} empty={<EmptyState title={t('signature.queueEmpty')} />} />
          ) : null}
        </Section>
        <Section flush title={t('signature.batchesTitle')}>
          {batches.status === 'loading' ? <Loading label={t('signature.loading')} rows={3} /> : null}
          {batches.status === 'error' ? <ErrorState error={batches.error} onRetry={batches.reload} title={t('signature.loadError')} /> : null}
          {batches.status === 'success' ? (
            <DataTable columns={batchColumns} rows={batches.data.items} rowKey={(b) => b.id} empty={<EmptyState title={t('signature.batchesEmpty')} />} />
          ) : null}
        </Section>
        {settings ? (
          <Section title={t('signature.settings.title')}>
            <SignatureSettingsForm key={JSON.stringify(settings)} initial={settings} onSaved={refresh} />
          </Section>
        ) : null}
      </div>
    </>
  );
}
